import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Paper,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import useIsMobile from "../../shared_modules/useIsMobile";
import { useApi } from "../../hooks/useApi";

const SelectBarber = ({ selectedBarber, setSelectedBarber }) => {
  const isMobile = useIsMobile();
  const api = useApi();

  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api("/barbers/barbers.php", { method: "GET" });
        setBarbers(res.data.barbers || []);
      } catch (e) {
        setError(e.message || "Failed to load barbers.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 5 }}>
        <CircularProgress size={18} sx={{ color: "text.secondary" }} />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Loading barbers...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 5 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Grid container spacing={2} sx={{ mt: 5 }}>
      {barbers.map((barber) => {
        const isSelected = selectedBarber?.user_id === barber.user_id;
        const hasStripe = barber.stripe_onboarded;

        return (
          <Grid key={barber.user_id} size={isMobile ? 6 : 4}>
            <Box
              component={Paper}
              elevation={isSelected ? 3 : 1}
              onClick={() => hasStripe && setSelectedBarber(barber)}
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                p: 3,
                borderRadius: 2,
                border: "0.1px solid",
                borderColor: isSelected ? "primary.main" : "customDivider",
                backgroundColor: isSelected ? "primary.main" : "offWhite",
                opacity: hasStripe ? 1 : 0.5,
                cursor: hasStripe ? "pointer" : "not-allowed",
                transition: "all 0.15s ease",
                "&:hover": hasStripe
                  ? {
                      borderColor: "primary.main",
                      boxShadow: 3,
                    }
                  : {},
              }}
            >
              {/* Avatar */}
              <Box
                sx={{
                  width: "5rem",
                  height: "5rem",
                  borderRadius: "50%",
                  backgroundColor: isSelected
                    ? "rgba(254,252,247,0.15)"
                    : "background.default",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: isSelected ? "primary.contrastText" : "text.third",
                  border: "1px solid",
                  borderColor: isSelected
                    ? "rgba(254,252,247,0.2)"
                    : "customDivider",
                  overflow: "hidden",
                  flexShrink: 0,
                }}
              >
                {barber.avatar_url ? (
                  <Box
                    component="img"
                    src={barber.avatar_url}
                    alt={`${barber.first_name} ${barber.last_name}`}
                    sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <>
                    {barber.first_name?.[0]}
                    {barber.last_name?.[0]}
                  </>
                )}
              </Box>

              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  mt: 1.5,
                  color: isSelected ? "primary.contrastText" : "text.primary",
                }}
              >
                {barber.first_name} {barber.last_name}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", mt: 0.5 }}>
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
                <StarIcon
                  sx={{ color: "secondary.main", fontSize: "0.9rem" }}
                />
              </Box>

              <Chip
                label={hasStripe ? "Available" : "Unavailable"}
                size="small"
                color={hasStripe ? "success" : "default"}
                sx={{ mt: 1 }}
              />
            </Box>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default SelectBarber;
